/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Grid of certification requirement checkboxes for appointment registration.
 * @module components/AppointmentRegistrationSections/Certifications/CertificationCheckboxGrid
 */

import { chunkArray } from "../../../utils/appointmentRegistrationUtils.js";
import { FIELD_VALIDATION_ERROR_BORDER_STYLE } from "../../../utils/formValidationConstants.js";
import { CERTIFICATIONS_ITEMS_PER_ROW } from "../../../utils/certificationSectionUtils.js";

/** Builds a stable React key for a row of certification checkbox items. */
const buildCertificationRowKey = (rowItems) =>
  rowItems.map((item) => item.value).join("-");

/**
 * @param {Object} props
 * @param {Array} props.certifications - Available certification options
 * @param {Array<string|number>} props.selectedCertifications - Selected certification values
 * @param {Function} props.onToggle - Called with certification value when toggled
 * @param {Function} [props.getFieldValidation] - Returns whether a field has validation errors
 * @param {Object} [props.missingRequiredFields] - Required-field flags keyed by field name
 * @param {Function} props.t - i18n translate function
 * @param {Object} props.styles - CSS module class map
 */
const CertificationCheckboxGrid = ({
  certifications,
  selectedCertifications,
  onToggle,
  getFieldValidation,
  missingRequiredFields,
  t,
  styles,
}) =>
  chunkArray(certifications, CERTIFICATIONS_ITEMS_PER_ROW).map((rowItems) => (
    <div
      key={buildCertificationRowKey(rowItems)}
      className={styles.checkboxRow}
    >
      {rowItems.map((certification) => {
        const requirementKey = `certificationOptionRequirement_${certification.value}`;
        const hasCertificationValidationError =
          getFieldValidation && getFieldValidation(requirementKey);

        return (
          <div
            key={certification.value}
            className={`${styles.checkboxItem} ${
              hasCertificationValidationError ? styles.inputError : ""
            }`}
            style={
              hasCertificationValidationError
                ? FIELD_VALIDATION_ERROR_BORDER_STYLE
                : undefined
            }
          >
            <input
              type="checkbox"
              id={certification.value}
              className={styles.checkbox}
              checked={selectedCertifications.includes(certification.value)}
              onChange={() => onToggle(certification.value)}
            />
            <label
              htmlFor={certification.value}
              className={styles.checkboxLabel}
            >
              {t(
                `components.certifications.certificationOption${
                  certification.value + 1
                }`,
              )}
              {missingRequiredFields &&
                missingRequiredFields[requirementKey] === true && (
                  <span className={styles.required}> *</span>
                )}
            </label>
          </div>
        );
      })}
    </div>
  ));

export default CertificationCheckboxGrid;
